import {
  ServiceProviderDetailsDateAvailability,
  ServiceProviderDetailsDayOfWeekAvailability,
} from 'Common/components/ServiceProviderDetails';
import dayjs from 'dayjs';
import {
  DayOfWeekAvailability,
  V2Gateway_QueryDateAvailabilityLazyQueryHookResult,
  V2Gateway_QueryDayOfWeekAvailabilityLazyQueryHookResult,
} from 'Generated/graphql';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { GetServiceProviderWithAvailability } from '../getServiceProviderWithAvailability';

dayjs.extend(utc);
dayjs.extend(timezone);

export type Bounds = {
  min: number;
  max: number;
}

export const getBoundsMin = (bounds: Bounds[]): number | undefined => {
  const currMin = bounds.reduce<number | undefined>(
    (tAcc, tCurr) => {
      if (tAcc !== undefined) {
        return tCurr.min < tAcc ? tCurr.min : tAcc;
      }
      return tCurr.min;
    }, undefined
  );

  return currMin;
};

export const getBoundsMax = (bounds: Bounds[]): number | undefined => {
  const currMax = bounds.reduce<number | undefined>(
    (tAcc, tCurr) => {
      if (tAcc !== undefined) {
        return tCurr.max > tAcc ? tCurr.max : tAcc;
      }
      return tCurr.max;
    }, undefined
  );

  return currMax;
};

export const getAvailabilityMin = <
  T extends Pick<DayOfWeekAvailability, 'availability'>
>(avail: T[]): [number | undefined, number | undefined] => {
  let index: number | undefined;
  const minFloor = avail.reduce<number | undefined>(
    (acc, curr, ix) => {
      const avail = curr.availability.timeUtcSecondsBounds.filter(
        b => b.min !== undefined && b.min !== null && b.max !== undefined &&
          b.min !== null
      ) as Bounds[];
      const currMin = getBoundsMin(avail);
      if (acc !== undefined) {
        if (currMin !== undefined) {
          if (currMin < acc) {
            index = ix;
            return currMin;
          }
          return acc;
        }
        return acc;
      }
      index = ix;
      return currMin;
    }, undefined
  );

  return [minFloor, index];
};

export const getAvailabilityMax = <
  T extends Pick<DayOfWeekAvailability, 'availability'>
>(avail: T[]): [number | undefined, number | undefined] => {
  let index: number | undefined;
  const maxCeil = avail.reduce<number | undefined>(
    (acc, curr, ix) => {
      const avail = curr.availability.timeUtcSecondsBounds.filter(
        b => b.min !== undefined && b.min !== null && b.max !== undefined &&
          b.min !== null
      ) as Bounds[];
      const currMax = getBoundsMax(avail);
      if (acc !== undefined) {
        if (currMax !== undefined) {
          if (currMax > acc) {
            index = ix;
            return currMax;
          }
          return acc;
        }
        return acc;
      }
      index = ix;
      return currMax;
    }, undefined
  );

  return [maxCeil, index];
};

export const isBoundsSurroundingBounds = (
  bounds: Bounds,
  surrounding: Bounds,
  inclusive = true,
): boolean => {
  return inclusive ?
    surrounding.min <= bounds.min && surrounding.max >= bounds.max :
    surrounding.min < bounds.min && surrounding.max > bounds.max;
};

// isBoundsWithinBoundsList returns true if ANY OF withinBounds surrounds bounds
export const isBoundsWithinBoundsList = (
  bounds: Bounds,
  withinBounds: Bounds[],
  inclusive = true,
): boolean => {
  if (withinBounds.length === 0) {
    return false;
  }

  const head = withinBounds[0];
  const tail = withinBounds.slice(1);

  const isWithin = isBoundsSurroundingBounds(bounds, head, inclusive);
  if (isWithin) {
    return true;
  }

  return isBoundsWithinBoundsList(bounds, tail, inclusive);
};

// Deprecated.
// Use src/js/react/common/hooks/useAvailabilityBoundries.tsx instead
export const collapseBounds = (bounds: Bounds[]): Bounds[] => {
  const oBounds = bounds.sort((a, b) => a.min - b.min);
  const cBounds = oBounds.map(b => {
    const prevFragmentOfContinuous = oBounds.find(p => p.max === b.min);
    if (prevFragmentOfContinuous) {
      return {
        min: prevFragmentOfContinuous.min,
        max: b.max,
      };
    }

    return b;
  });

  return cBounds;
};

export const isBoundsIntersectingBounds = (
  a: Bounds,
  b: Bounds,
  inclusiveStart = true,
  inclusiveEnd = true,
): boolean => {
  switch (true) {
    case inclusiveStart && inclusiveEnd: {
      const isIntersecting =
        (a.min >= b.min && a.min <= b.max) ||
          (a.max <= b.max && a.max >= b.min);
      return isIntersecting;
    }
    case inclusiveStart && !inclusiveEnd: {
      const isIntersecting =
        (a.min >= b.min && a.min <= b.max) ||
          (a.max < b.max && a.max > b.min);
      return isIntersecting;
    }
    case !inclusiveStart && inclusiveEnd: {
      const isIntersecting =
        (a.min >= b.min && a.min < b.max) ||
          (a.max <= b.max && a.max > b.min);
      return isIntersecting;
    }
    case !inclusiveStart && !inclusiveEnd: {
      const isIntersecting =
        (a.min > b.min && a.min < b.max) ||
          (a.max < b.max && a.max > b.min);
      return isIntersecting;
    }
    default:
      return false;
  }
};

export const isBoundsIntersectingBoundsList = (
  a: Bounds,
  b: Bounds[],
  inclusiveStart = true,
  inclusiveEnd = true,
): boolean => {
  if (b.length === 0) {
    return false;
  }

  const head = b[0];
  const tail = b.slice(1);
  const isIntersecting = isBoundsIntersectingBoundsList(a, tail, inclusiveStart, inclusiveEnd);
  if (isIntersecting) {
    return true;
  }

  return isBoundsIntersectingBounds(a, head, inclusiveStart, inclusiveEnd);
};

type GQLDayOfWeekAvailability = NonNullable<
  V2Gateway_QueryDayOfWeekAvailabilityLazyQueryHookResult[1]['data']
>['queryDayOfWeekAvailability']['dayOfWeekAvailability']

type GQLDateAvailability = NonNullable<
  V2Gateway_QueryDateAvailabilityLazyQueryHookResult[1]['data']
>['queryDateAvailability']['dateAvailability']

type dowAvailability = Pick<
  GQLDayOfWeekAvailability[number], 'timezone' | 'dayOfWeek' | 'availability' | 'ownerUid'
>[]

type dateAvailability = Pick<
  GQLDateAvailability[number], 'timezone' | 'date' | 'availability' | 'ownerUid'
>[]

export const getAvailabilityBoundryForDateForOwner = (
  startDateTime: number,
  ownerUid: string,
  dayOfWeekAvailability: dowAvailability,
  dateAvailability: Pick<
    GQLDateAvailability[number], 'timezone' | 'date' | 'availability' | 'ownerUid'
  >[],
  isStartBoundry: boolean,
): dayjs.Dayjs => {
  const spAvailTz = dayOfWeekAvailability ?
    dayOfWeekAvailability[0].timezone :
    'America/Winnipeg';
  const startDateTimeDayJs = dayjs.unix(startDateTime);
  const bod = startDateTimeDayJs
    .clone()
    .tz(spAvailTz)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
    .set('date', startDateTimeDayJs.date())
    .set('month', startDateTimeDayJs.month());

  const spDow = dayOfWeekAvailability.filter(
    d => d.ownerUid === ownerUid,
  );
  const dowAvail = spDow.find(
    a => a.dayOfWeek === bod.day()
  );
  const dowAvailability = dowAvail?.availability.timeUtcSecondsBounds.reduce<
    dowAvailability[number]['availability']['timeUtcSecondsBounds'][number] | null
  >(
    (a, c) => {
      if (a) {
        if (isStartBoundry) {
          const accMin = bod.clone().add(a.min as number, 's');
          const returnCurrent =
            bod.clone().add(c.min as number, 's').isBefore(accMin);
          return returnCurrent ? c : a;
        } else {
          const accMax = bod.clone().add(a.max as number, 's');
          const returnCurrent =
            bod.clone().add(c.max as number, 's').isAfter(accMax);
          return returnCurrent ? c : a;
        }
      } else {
        return c;
      }
    }, null
  );
  const dowAvailabilityBoundry = (isStartBoundry ? dowAvailability?.min : dowAvailability?.max) as number;

  let dateAvailabilityBoundry = null;
  const spDa = dateAvailability.filter(
    d => d.ownerUid === ownerUid,
  );
  const availForDate = spDa.find(
    a => dayjs.unix(a.date).utc().isSame(startDateTimeDayJs.clone().utc(), 'd')
  );
  if (availForDate) {
    const dateAvailability =
      availForDate.availability.timeUtcSecondsBounds.reduce<dateAvailability[number]['availability']['timeUtcSecondsBounds'][number] | null>(
        (a, c) => {
          if (a) {
            if (isStartBoundry) {
              const accMin = bod.clone().add(a.min as number, 's');
              const returnCurrent =
                bod.clone().add(c.min as number, 's').isBefore(accMin);
              return returnCurrent ? c : a;
            } else {
              const accMax = bod.clone().add(a.max as number, 's');
              const returnCurrent =
                bod.clone().add(c.max as number, 's').isAfter(accMax);
              return returnCurrent ? c : a;
            }
          } else {
            return c;
          }
        }, null
      );
    dateAvailabilityBoundry = (isStartBoundry ? dateAvailability?.min : dateAvailability?.max) as number;
  }

  let minAvailabilityStart = null;
  if (dateAvailabilityBoundry) {
    minAvailabilityStart = isStartBoundry ? Math.min(
      dowAvailabilityBoundry,
      dateAvailabilityBoundry,
    ) : Math.max(
      dowAvailabilityBoundry,
      dateAvailabilityBoundry,
    );
  } else {
    minAvailabilityStart = dowAvailabilityBoundry;
  }

  const minAvailabilityStartDayJs =
    bod.clone().add(minAvailabilityStart, 's');

  return minAvailabilityStartDayJs;
};

export const availabilityToEpoch = (
  day: dayjs.Dayjs,
  avail: Bounds[],
): [number, number][] => {
  const bod = day
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);

  return avail.map<[number, number]>(b => {
    const m = bod.unix() + b.min;
    const a = bod.unix() + b.max;
    return [m, a];
  });
};

export const getAvailabilityBoundriesForDate = (
  startDateTime: number,
  dayOfWeekAvailability: GetServiceProviderWithAvailability['dayOfWeekAvailability'],
  dateAvailability: Pick<
    GQLDateAvailability[number], 'timezone' | 'date' | 'availability' | 'ownerUid'
  >[],
): [number, number][] => {
  const startDateTimeDayJs = dayjs.unix(startDateTime);
  const bod = startDateTimeDayJs
    .clone()
    .startOf('d');

  const dowAvail = dayOfWeekAvailability.find(
    a => a.dayOfWeek === startDateTimeDayJs.day()
  );
  const availForDate = dateAvailability.find(
    a => {
      const av = dayjs.utc(a.date * 1000);
      const selectedDate = bod;
      return av.isSame(selectedDate, 'd');
    }
  );

  const avail = availForDate?.availability?.timeUtcSecondsBounds ?
    availForDate :
    dowAvail;
  if (!avail) {
    return [];
  }

  const boundsUnix = availabilityToEpoch(
    bod,
    avail.availability.timeUtcSecondsBounds as Bounds[],
  );

  return boundsUnix;
};

export const getAvailabilityBoundriesForDateForOwner = (
  startDateTime: number,
  ownerUid: string,
  dayOfWeekAvailability: GetServiceProviderWithAvailability['dayOfWeekAvailability'],
  dateAvailability: Pick<
    GQLDateAvailability[number], 'timezone' | 'date' | 'availability' | 'ownerUid'
  >[],
): [number, number][] => {
  const spDow = dayOfWeekAvailability.filter(
    d => d.ownerUid === ownerUid,
  );
  const spDa = dateAvailability.filter(
    d => d.ownerUid === ownerUid,
  );

  const boundsUnix = getAvailabilityBoundriesForDate(startDateTime, spDow, spDa);

  return boundsUnix;
};

export const getIsUnavailableAtTime = (
  start: number,
  end: number,
  unavailableTimeBounds: Bounds[],
): boolean => {
  const isIntersecting = isBoundsIntersectingBoundsList(
    { min: start, max: end },
    unavailableTimeBounds,
    false,
    true,
  );

  const isWrapping = unavailableTimeBounds.some(ua => isBoundsSurroundingBounds(
    ua,
    { min: start, max: end },
    true,
  ));

  const isUnavailable = isIntersecting || isWrapping;

  return isUnavailable;
};

export const getIsUnavailableByDateAvailability = (
  start: number,
  end: number,
  dateAvailability: Pick<
    ServiceProviderDetailsDateAvailability[number],
    'date' | 'availability'
  >[],
): boolean | undefined => {
  const valDayJs = dayjs.unix(start).startOf('d');
  const dateAvail = dateAvailability.find(
    d => {
      const dateAvail = dayjs.utc(d.date * 1000);
      const isSameDate = dateAvail.isSame(valDayJs, 'D');
      return isSameDate;
    }
  );

  // Case where they have no date specific availbility configured
  if (!dateAvail) {
    return undefined;
  }

  if (!dateAvail.availability.timeUtcSecondsBounds.length) {
    return true;
  }

  const bod = dayjs
    .unix(start)
    .clone()
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  const availForStartDay = availabilityToEpoch(
    bod,
    dateAvail.availability.timeUtcSecondsBounds as Bounds[],
  ).map(([start, end]) => ({
    min: start,
    max: end,
  }));

  const isWithinAvailability = isBoundsWithinBoundsList(
    {
      min: start,
      max: end,
    },
    availForStartDay,
    true,
  );
  if (!isWithinAvailability) {
    return true;
  }

  return false;
};

export const getIsUnavailableByDayOfWeekAvailability = (
  start: number,
  end: number,
  dayOfWeekAvailability: Pick<
    ServiceProviderDetailsDayOfWeekAvailability[number],
    'availability' | 'dayOfWeek'
  >[],
): boolean => {
  const valDayJs = dayjs.unix(start);
  const dowAvail = dayOfWeekAvailability.find(
    d => d.dayOfWeek === valDayJs.day(),
  );

  if (!dowAvail?.availability.timeUtcSecondsBounds.length) {
    return true;
  }

  const bod = dayjs
    .unix(start)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  const availForStartDay = availabilityToEpoch(
    bod,
    dowAvail.availability.timeUtcSecondsBounds as Bounds[],
  ).map(([start, end]) => ({
    min: start,
    max: end,
  }));

  const isWithinAvailability = isBoundsWithinBoundsList(
    {
      min: start,
      max: end,
    },
    availForStartDay,
    true,
  );
  if (!isWithinAvailability) {
    return true;
  }

  return false;
};
